import { zodResolver } from '@hookform/resolvers/zod';
import { useId } from 'react';
import { z } from 'zod';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import { passwordRegEx } from '../../utils/RegularExpressions';

const invitePasswordSetupSchema = z
  .object({
    tempPassword: z.string().trim().min(5, 'Please insert valid temporary password'),
    password: z
      .string()
      .regex(passwordRegEx, 'Must Contain 6 Characters, One Uppercase, One Lowercase, One Number and one special case Character'),
    passwordConfirmation: z.string().min(1, 'Required'),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Password must match',
    path: ['passwordConfirmation'],
  });

export type InvitePasswordSetupFormValues = z.infer<typeof invitePasswordSetupSchema>;

interface InvitePasswordSetupFormProps {
  onSubmit: (values: InvitePasswordSetupFormValues) => void;
  isSubmitting?: boolean;
  temporaryPasswordErrorMessage?: string;
  submitErrorMessage?: string;
  submitLabel?: string;
}

export default function InvitePasswordSetupForm({
  onSubmit,
  isSubmitting = false,
  temporaryPasswordErrorMessage = '',
  submitErrorMessage = '',
  submitLabel = 'Create Account',
}: InvitePasswordSetupFormProps) {
  const tempPasswordId = useId();
  const passwordId = useId();
  const confirmPasswordId = useId();

  return (
    <HookFormProvider<InvitePasswordSetupFormValues>
      mode='all'
      resolver={zodResolver(invitePasswordSetupSchema)}
      defaultValues={{
        tempPassword: '',
        password: '',
        passwordConfirmation: '',
      }}
    >
      <HookForm<InvitePasswordSetupFormValues> className='bg-white rounded-lg shadow-2xl p-8 relative' onSubmit={onSubmit}>
        <h2 className='mt-0'>Hey there ! Let's choose a password for your account.</h2>
        {submitErrorMessage && <div className='mb-5 text-sm text-red-600'>{submitErrorMessage}</div>}
        <div className='mb-5'>
          <HookInput
            id={tempPasswordId}
            type='password'
            name='tempPassword'
            label='Temporary Password'
            error={temporaryPasswordErrorMessage}
          />
        </div>
        <div className='mb-5'>
          <HookInput id={passwordId} type='password' name='password' label='Password' />
        </div>
        <div className='mb-5'>
          <HookInput id={confirmPasswordId} type='password' name='passwordConfirmation' label='Confirm Password' />
        </div>
        <Button disabled={isSubmitting} label={isSubmitting ? 'Please Wait...' : submitLabel} btnStyle='btn-primary' type='submit' />
      </HookForm>
    </HookFormProvider>
  );
}
